import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../config/api-endpoints';

export type AnalyticsPeriod = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export interface SalesAnalytics {
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  peakHour: number;
  salesByHour: { hour: number; sales: number; orders: number }[];
  salesByDay: { date: string; sales: number; orders: number }[];
}

export interface SalesAnalyticsReport {
  id?: number;
  barId?: string;
  date?: string;
  totalSales?: number;
  totalOrders?: number;
  averageOrderValue?: number;
  totalCustomers?: number;
  peakHour?: number;
  slowestHour?: number;
  createdAt?: string;
}

export interface DrinkPerformanceReport {
  id?: number;
  drinkId?: number;
  drinkName?: string;
  category?: string;
  date?: string;
  period?: string;
  quantitySold?: number;
  revenueGenerated?: number;
  currentStock?: number;
  lowStockThreshold?: number;
  lowStockAlerts?: number;
  averageRating?: number;
  createdAt?: string;
  updatedAt?: string;
  lowStock?: boolean;
  revenuePerUnit?: number;
}

export interface DrinkPopularityReport {
  id?: number;
  drinkId?: number;
  drinkName?: string;
  totalOrders?: number;
  totalRevenue?: number;
  date?: string;
  createdAt?: string;
}

export interface StaffPerformanceAnalyticsReport {
  id?: number;
  barId?: string;
  staffId?: number;
  staffName?: string;
  date?: string;
  ordersServed?: number;
  totalSales?: number;
  tipsReceived?: number;
  hoursWorked?: number;
  createdAt?: string;
}

export interface InventoryAlerts {
  lowStockCount: number;
  outOfStockCount: number;
  alerts: any[];
}

export interface StaffPerformance {
  staffId: number;
  staffName: string;
  ordersServed: number;
  totalSales: number;
  averageServiceTime: number;
  customerRating: number;
  date: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  constructor(private http: HttpClient) { }

  getDashboardSummary(date?: string): Observable<any> {
    const params: any = {};
    if (date) params.date = date;
    return this.http.get<any>(`analytics:${API_ENDPOINTS.ANALYTICS.DASHBOARD}`, { params });
  }

  getSalesAnalytics(startDate?: string, endDate?: string): Observable<SalesAnalytics> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<SalesAnalytics>(`analytics:${API_ENDPOINTS.ANALYTICS.SALES}`, { params });
  }

  getSalesReport(period: AnalyticsPeriod): Observable<SalesAnalyticsReport[]> {
    const params = new HttpParams().set('period', period);
    return this.http.get<SalesAnalyticsReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.SALES}`, { params });
  }

  getRevenueTrends(period: AnalyticsPeriod = 'DAILY', days: number = 30): Observable<SalesAnalyticsReport[]> {
    return this.http.get<SalesAnalyticsReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.REVENUE_TRENDS}`, {
      params: { period, days: days.toString() }
    });
  }

  getCategoryPerformance(period: AnalyticsPeriod = 'DAILY', startDate?: string, endDate?: string): Observable<any[]> {
    let params = new HttpParams().set('period', period);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any[]>(`analytics:${API_ENDPOINTS.ANALYTICS.CATEGORY_PERFORMANCE}`, { params });
  }

  getInventoryAlerts(): Observable<InventoryAlerts> {
    return this.http.get<InventoryAlerts>(`analytics:${API_ENDPOINTS.ANALYTICS.INVENTORY_ALERTS}`);
  }

  getStaffPerformance(): Observable<StaffPerformance[]> {
    return this.http.get<StaffPerformance[]>(`analytics:${API_ENDPOINTS.ANALYTICS.STAFF_PERFORMANCE}`);
  }

  getStaffSales(): Observable<any[]> {
    return this.http.get<any[]>(`analytics:${API_ENDPOINTS.ANALYTICS.STAFF_SALES}`);
  }

  getStaffSalesReport(period: AnalyticsPeriod): Observable<StaffPerformanceAnalyticsReport[]> {
    const params = new HttpParams().set('period', period);
    return this.http.get<StaffPerformanceAnalyticsReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.STAFF_SALES}`, { params });
  }

  getStaffPerformanceByPeriod(period: AnalyticsPeriod): Observable<any[]> {
    const params = new HttpParams().set('period', period);
    return this.http.get<any[]>(`analytics:${API_ENDPOINTS.ANALYTICS.STAFF_SALES}`, { params });
  }

  getTopPerformers(limit: number = 5): Observable<any[]> {
    return this.http.get<any[]>(`analytics:${API_ENDPOINTS.ANALYTICS.TOP_PERFORMERS_LIST}`, {
      params: { limit: limit.toString() }
    });
  }

  getStaffRatings(): Observable<any[]> {
    return this.http.get<any[]>(`analytics:${API_ENDPOINTS.ANALYTICS.STAFF_RATINGS}`);
  }

  getDrinkPopularity(startDate?: string, endDate?: string): Observable<DrinkPopularityReport[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<DrinkPopularityReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.DRINK_POPULARITY}`, { params });
  }

  getDrinkRevenue(startDate?: string, endDate?: string): Observable<DrinkPopularityReport[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<DrinkPopularityReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.DRINK_REVENUE}`, { params });
  }

  getDrinkPerformance(period: AnalyticsPeriod): Observable<DrinkPerformanceReport[]> {
    const params = new HttpParams().set('period', period);
    return this.http.get<DrinkPerformanceReport[]>(`analytics:${API_ENDPOINTS.ANALYTICS.INVENTORY}`, { params });
  }

  generateDailyReport(): Observable<string> {
    return this.http.post<string>(`analytics:${API_ENDPOINTS.ANALYTICS.REPORTS_DAILY}`, null);
  }

  generateMonthlyReport(): Observable<string> {
    return this.http.post<string>(`analytics:${API_ENDPOINTS.ANALYTICS.REPORTS_MONTHLY}`, null);
  }
}
